import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  ChartBar,
  UsersThree,
  Clock,
  CalendarBlank,
  TrendUp,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useBusiness } from '../../../context/BusinessContext';
import { useThemeStyles } from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

const noHoverItemSx = { '&:hover': { backgroundColor: 'transparent' }, cursor: 'default' };

const getStageColor = (stage) => {
  const colors = {
    enquired: '#9E9E9E',
    in_progress: '#00BCD4',
    completed: '#4CAF50',
    new_enquiry: '#9E9E9E',
    contacted: '#2196F3',
    qualified: '#FF9800',
    agreement_sent: '#9C27B0',
    awaiting_response: '#F44336',
    booked: '#4CAF50',
    deposit_paid: '#8BC34A',
    followup: '#607D8B',
    archived: '#757575',
  };
  return colors[stage] || '#666';
};

const getStageLabel = (stage) => {
  const labels = {
    enquired: 'Enquired',
    in_progress: 'In Progress',
    completed: 'Completed',
    new_enquiry: 'New Enquiry',
    contacted: 'Contacted',
    qualified: 'Qualified',
    agreement_sent: 'Agreement Sent',
    awaiting_response: 'Awaiting Response',
    booked: 'Booked',
    deposit_paid: 'Deposit Paid',
    followup: 'Follow-up',
    archived: 'Archived',
  };
  return labels[stage] || (stage ? stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: format(start, 'yyyy-MM-dd'),
    to: format(end, 'yyyy-MM-dd'),
  };
};

const CRMAnalytics = () => {
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  useEffect(() => {
    if (selectedBusiness) {
      fetchAnalytics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = selectedBusiness?.business?._id ? { businessId: selectedBusiness.business._id } : {};
      if (dateRange.from) params.dateFrom = dateRange.from;
      if (dateRange.to) params.dateTo = dateRange.to;
      const response = await api.get('/api/crm/analytics', { params });
      setAnalytics(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) return null;

  const leadsByStage = Array.isArray(analytics.leadsByStage) ? analytics.leadsByStage : [];
  const leadsBySource = Array.isArray(analytics.leadsBySource) ? analytics.leadsBySource : [];
  const _leadsByPriority = Array.isArray(analytics.leadsByPriority) ? analytics.leadsByPriority : [];
  const teamPerformance = Array.isArray(analytics.teamPerformance) ? analytics.teamPerformance : [];
  const remindersByMember = Array.isArray(analytics.remindersByMember) ? analytics.remindersByMember : [];
  const bookingsThisMonthByMember = Array.isArray(analytics.bookingsThisMonthByMember) ? analytics.bookingsThisMonthByMember : [];

  const _dateRangeLabel = dateRange.from && dateRange.to
    ? `${format(new Date(dateRange.from), 'd MMM yyyy')} to ${format(new Date(dateRange.to), 'd MMM yyyy')}`
    : format(new Date(), 'MMMM yyyy');

  return (
    <Box sx={{ ...styles.container.page, py: 2 }}>
      <Box sx={{ ...styles.layout.flexBetween, flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Pipeline analytics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateRange.from}
            onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateRange.to}
            onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Stages */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Stages"
            icon={<CalendarBlank size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            <List dense>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Enquiries added"
                  secondary={analytics.enquiriesAddedThisMonth ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Completed"
                  secondary={analytics.completedThisMonth ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'h6', fontWeight: 600, color: 'success.main' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Lost couples"
                  secondary={analytics.lostCouplesThisMonth ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'h6', fontWeight: 600, color: 'error.main' }}
                />
              </ListItem>
              {leadsByStage.filter((s) => !['enquired', 'completed', 'archived'].includes(s._id)).map((stage) => (
                <ListItem key={stage._id} sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: getStageColor(stage._id),
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={getStageLabel(stage._id)}
                    secondary={stage.totalValue ? `£${stage.totalValue.toLocaleString()}` : null}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                  <Chip label={stage.count} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                </ListItem>
              ))}
            </List>
          </DashboardCard>
        </Grid>

        {/* Team workload */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Team workload"
            icon={<UsersThree size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            {teamPerformance.length > 0 ? (
              <List dense>
                {teamPerformance.slice(0, 5).map((member, idx) => (
                  <ListItem key={member.memberId || idx} sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                    <ListItemText
                      primary={
                        <Box sx={styles.layout.flexBetween}>
                          <Typography variant="body2" fontWeight={500}>
                            {member.memberName || 'Unassigned'}
                          </Typography>
                          <Chip label={member.totalLeads} size="small" color="primary" variant="outlined" />
                        </Box>
                      }
                      secondary={`${member.completedLeads} completed, ${member.completionRate}% rate`}
                      secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No assigned couples yet
              </Typography>
            )}
          </DashboardCard>
        </Grid>

        {/* Reminders by Member */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Reminders by Team"
            icon={<Clock size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            {remindersByMember.length > 0 ? (
              <List dense>
                {remindersByMember.map((m, idx) => (
                  <ListItem key={m.memberId || idx} sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                    <ListItemText
                      primary={m.memberName || 'Unassigned'}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {m.overdue > 0 && (
                            <Chip label={`${m.overdue} overdue`} size="small" color="error" variant="outlined" />
                          )}
                          {m.upcoming > 0 && (
                            <Chip label={`${m.upcoming} upcoming`} size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active reminders
              </Typography>
            )}
          </DashboardCard>
        </Grid>

        {/* Bookings in Range */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Bookings"
            icon={<CalendarBlank size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" fontWeight={600} color="primary.main">
                {analytics.totalBookingsThisMonth ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total in range
              </Typography>
            </Box>
            {bookingsThisMonthByMember.length > 0 ? (
              <List dense>
                {bookingsThisMonthByMember.map((m, idx) => (
                  <ListItem key={m.memberId || idx} sx={{ px: 0, py: 0.5, ...noHoverItemSx }}>
                    <ListItemText
                      primary={m.memberName || 'Unassigned'}
                      secondary={`${m.count} booking${m.count !== 1 ? 's' : ''}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No bookings assigned to team members
              </Typography>
            )}
          </DashboardCard>
        </Grid>

        {/* Pipeline Overview */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Pipeline"
            icon={<ChartBar size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            {leadsByStage.length > 0 ? (
              <List dense>
                {leadsByStage.map((stage) => (
                  <ListItem key={stage._id} sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: getStageColor(stage._id),
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={getStageLabel(stage._id)}
                      secondary={stage.totalValue ? `£${stage.totalValue.toLocaleString()}` : null}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                    />
                    <Chip label={stage.count} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No couples in pipeline
              </Typography>
            )}
          </DashboardCard>
        </Grid>

        {/* Key Metrics + Sources */}
        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Key Metrics"
            icon={<TrendUp size={22} weight="regular" />}
            menuItems={[]}
            sx={{ '&:hover': { backgroundColor: 'inherit', borderColor: 'inherit' } }}
          >
            <List dense>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Couples in pipeline"
                  secondary={analytics.totalLeads ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Conversion rate"
                  secondary={`${analytics.conversionRate ?? 0}%`}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'h6', fontWeight: 600, color: 'success.main' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1, ...noHoverItemSx }}>
                <ListItemText
                  primary="Overdue reminders"
                  secondary={analytics.overdueReminders ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{
                    variant: 'h6',
                    fontWeight: 600,
                    color: (analytics.overdueReminders ?? 0) > 0 ? 'error.main' : 'text.primary',
                  }}
                />
              </ListItem>
            </List>
            {leadsBySource.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                  Sources
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {leadsBySource.map((s) => (
                    <Chip
                      key={s._id}
                      label={`${s._id}: ${s.count}`}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </Box>
              </>
            )}
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CRMAnalytics;
